import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAutoSave, saveToLocalStorage } from "@/hooks/useAutoSave";
import { effectiveMetricsFor } from "./dashboardModel";

const REFRESH_MS = 30000;

export const useDashboardData = (userId, currentInstance) => {
  const [metrics, setMetrics] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [goalReached, setGoalReached] = useState(false);

  const effectiveMetrics = useMemo(() => effectiveMetricsFor(metrics, currentInstance), [metrics, currentInstance]);

  useAutoSave(user, (data) => saveToLocalStorage(`ism-user-${userId}`, data), REFRESH_MS);

  const refresh = useCallback(async () => {
    if (userId === "new") { setLoading(false); return; }
    try {
      setError(null);
      const t = Date.now();
      const [metricsRes, userRes] = await Promise.all([axios.get(`${API}/dashboard/${userId}?t=${t}`), axios.get(`${API}/users/${userId}?t=${t}`)]);
      setMetrics(metricsRes.data);
      setUser(userRes.data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    if (effectiveMetrics && effectiveMetrics.progressPercentage >= 100 && !goalReached) {
      const timer = setTimeout(() => { setShowGoalModal(true); setGoalReached(true); }, 1000);
      return () => clearTimeout(timer);
    }
  }, [effectiveMetrics, goalReached]);

  return { user, loading, error, effectiveMetrics, refresh, showGoalModal, closeGoalModal: () => setShowGoalModal(false) };
};
